/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { isDesktopBreakpoint } from './utilities.js';

/**
 * In-place product image zoom for desktop: click toggles a hi-res
 * `background-image` that follows the cursor, confined to the wrapper's own
 * bounds. No dialog/lightbox. Reads the hi-res URL from the wrapped `<img>`'s
 * `data_max_resolution` attribute (already rendered by `product-media.liquid`).
 */
export class MediaZoom extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!isDesktopBreakpoint()) return;

    const image = this.#image;
    if (!image) return;

    const highRes = image.getAttribute('data_max_resolution');
    if (!highRes) return;

    this.style.backgroundImage = `url("${highRes}")`;

    if (!this.closest('[data-loop-clone]')) {
      if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', image.alt || '');
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#toggleZoom, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('mousemove', this.#handleMouseMove, { signal });
    this.addEventListener('mouseleave', this.#reset, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {Event} event */
  #toggleZoom = (event) => {
    event.preventDefault();
    if (this.hasAttribute('zoomed')) {
      this.#reset();
    } else {
      if (event instanceof MouseEvent) this.#updateBackgroundPosition(event);
      this.setAttribute('zoomed', '');
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.#toggleZoom(event);
  };

  /** @param {MouseEvent} event */
  #handleMouseMove = (event) => {
    if (!this.hasAttribute('zoomed')) return;
    this.#updateBackgroundPosition(event);
  };

  /** @param {MouseEvent} event */
  #updateBackgroundPosition = (event) => {
    const rect = this.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.style.backgroundPosition = `${x}% ${y}%`;
  };

  #reset = () => {
    this.removeAttribute('zoomed');
  };
}

if (!customElements.get('media-zoom')) {
  customElements.define('media-zoom', MediaZoom);
}
